/**
 * @voltxampere/engine — Analog multiplexer DC stamp (Sprint 43, ADR-0063)
 *
 * Sprint 43 introduces the `analog-mux-8` and `analog-mux-16` canonical
 * engine types to land the 4051 / 4052 / 4067 / 74HC4051 family with
 * full DC simulation. Both types share the same underlying stamp
 * implementation; the type-string discriminator selects channel /
 * select-pin counts at registration time.
 *
 * The stamp follows Sprint 4's voltage-source augmented row pattern
 * verbatim: one augmented MNA row per mux carries the common output
 * as a known voltage source whose value is recomputed each Newton
 * iteration from `xPrev[selected_channel]` and the threshold-applied
 * select-pin voltages.
 *
 * Pin layout (per type):
 *
 *   analog-mux-8  (4051, 4052 single-section, 74HC4051):
 *     ch0..ch7  - 8 analog input channels
 *     sel_a, sel_b, sel_c - 3 binary select pins (LSB first)
 *     enable    - chip-enable (default active LOW per 4051 datasheet)
 *     common    - selected channel passthrough output
 *     Total: 13 pins.
 *
 *   analog-mux-16 (4067):
 *     ch0..ch15 - 16 analog input channels
 *     sel_a, sel_b, sel_c, sel_d - 4 binary select pins
 *     enable    - chip-enable
 *     common    - selected channel passthrough output
 *     Total: 22 pins.
 *
 * The sel pins are read from `xPrev` and thresholded against
 * `params.threshold` (default 2.5 V) — exactly the same digital-gate
 * boolean discriminator. The resulting integer in `[0, channelCount)`
 * is the channel index. Enable polarity is configurable via
 * `params.enableActiveLow` (default `true` per 4051/4067 datasheet);
 * the stamp forces the augmented-row output to 0 V when disabled (see
 * ADR-0063 §"Disabled state semantics" for the high-impedance
 * approximation rationale).
 *
 * Newton convergence note: like `digital-gate`, the `v >= threshold`
 * comparison on select pins creates a step function in the iteration
 * map. Sentinels (`sprint-43-analog-mux-4051`, `sprint-43-analog-mux-
 * 4067`) drive select pins to clear-cut 0 V / 5 V and channel inputs
 * to clear-cut DC values to avoid the borderline case.
 *
 * AC + transient stamps are intentionally not registered. AC small-
 * signal modelling around a non-smooth select-pin threshold is
 * undefined; transient propagation through the mux needs the broader
 * smooth-threshold work that Sprint 50+ ships. Both raise the engine's
 * "no stamp registered" error if encountered.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { defineDCStamp } from "./definition.js";
import { getTerminalNetId, getNumericParam } from "./terminal-utils.js";
/**
 * Read an MNA node voltage from the previous Newton iterate, treating
 * ground (`idx === -1`) as 0 V exactly. Mirrors the helper inside
 * `digital-gate.ts` so both stamps follow the same convention.
 */
function nodeVoltage(xPrev, idx) {
    if (idx < 0)
        return 0;
    return xPrev[idx] ?? 0;
}
const SELECT_PIN_LETTERS = ["a", "b", "c", "d"];
/**
 * Build the analog-mux DC stamp for a fixed channel count. Each call
 * returns a stamp closure pinned to its own `(channelCount,
 * selectPinCount)` pair so the stamp body never branches on per-call
 * variant detection — registration time bakes the dimensions in.
 */
function makeAnalogMuxDCStamp(channelCount, selectPinCount) {
    return (ctx, component) => {
        const params = component.params;
        const threshold = getNumericParam(component, "threshold", 2.5);
        // Read M select pins from xPrev and decode to integer index in
        // [0, channelCount). LSB is `sel_a` (matches the 4051/4067 "S0"
        // convention on the TI / NXP datasheet).
        let selected = 0;
        for (let i = 0; i < selectPinCount; i += 1) {
            const letter = SELECT_PIN_LETTERS[i];
            if (letter === undefined)
                continue; // unreachable for selectPinCount ≤ 4
            const idx = ctx.nodeIndex(getTerminalNetId(component, `sel_${letter}`));
            const v = nodeVoltage(ctx.xPrev, idx);
            if (v >= threshold)
                selected |= 1 << i;
        }
        // Read enable pin. Default polarity is active LOW per 4051/4067.
        const enableActiveLowRaw = params.enableActiveLow;
        const enableActiveLow = typeof enableActiveLowRaw === "boolean" ? enableActiveLowRaw : true;
        const enableIdx = ctx.nodeIndex(getTerminalNetId(component, "enable"));
        const vEnable = nodeVoltage(ctx.xPrev, enableIdx);
        const enabled = enableActiveLow ? vEnable < threshold : vEnable >= threshold;
        // Read selected channel voltage. When disabled, force the output
        // augmented row to 0 V (ADR-0063 §"Disabled state semantics" —
        // documented high-impedance approximation).
        let vChannel = 0;
        if (enabled) {
            const chPin = `ch${selected.toString()}`;
            const chIdx = ctx.nodeIndex(getTerminalNetId(component, chPin));
            vChannel = nodeVoltage(ctx.xPrev, chIdx);
        }
        // Stamp common output as augmented-row voltage source. The pattern
        // mirrors `voltageSourceDCStamp` in linear/voltage-source.ts:
        // B[out, k] = +1, C[k, out] = +1, rhs[k] = V * sourceScale. There
        // is no `neg` terminal — the mux's common output is always
        // referenced to the implicit MNA ground.
        const out = ctx.nodeIndex(getTerminalNetId(component, "common"));
        const k = ctx.vSourceIndex(component.id);
        ctx.addY(out, k, 1);
        ctx.addY(k, out, 1);
        ctx.rhs[k] = (ctx.rhs[k] ?? 0) + vChannel * ctx.sourceScale;
        void channelCount; // documented but not read at runtime — selectPinCount governs the decode loop
    };
}
/** 8-channel analog mux (4051 / 4052 single section / 74HC4051). */
export const analogMux8DCStamp = makeAnalogMuxDCStamp(8, 3);
/** 16-channel analog mux (4067). */
export const analogMux16DCStamp = makeAnalogMuxDCStamp(16, 4);
/**
 * Both definitions carry exactly one `["vsource"]` augmented row — same
 * augmentation contract as the canonical voltage-source stamp. The
 * planner allocates one extra MNA row per mux through the
 * `vSourceIndex` lookup the stamp uses above.
 */
export const analogMux8DCStampDefinition = defineDCStamp("analog-mux-8", analogMux8DCStamp, [
    "vsource",
]);
export const analogMux16DCStampDefinition = defineDCStamp("analog-mux-16", analogMux16DCStamp, [
    "vsource",
]);
//# sourceMappingURL=analog-mux.js.map